/**
 * Supabase CLI secret broker contracts (#520, on #428 / #445).
 *
 * Connector first. The Supabase CLI runs only for the few operations the
 * connector cannot do correctly — above all exact repository-version
 * `db push --linked` — and only through the node Secret Broker, which injects
 * SUPABASE_DB_PASSWORD into one allowlisted child process on the one node the
 * owner designated. CommandOS keeps non-secret node_bound metadata only; the
 * password lives in the OS-native credential store on that node.
 *
 * Nothing here accepts, returns or builds a command line from caller text: a
 * requester names an intent, and the argv is fixed per operation.
 */

/** Operations the broker may run. Adding one is a product change, not a config change. */
export const SUPABASE_CLI_OPERATIONS = [
  'migration_list',
  'db_push_dry_run',
  'db_push_apply',
  'link',
] as const;
export type SupabaseCliOperation = (typeof SUPABASE_CLI_OPERATIONS)[number];

/** Supabase project refs are 20 lowercase letters. Anything else fails closed. */
export const SUPABASE_PROJECT_REF_PATTERN = /^[a-z]{20}$/;

export function isSupabaseProjectRef(value: unknown): value is string {
  return typeof value === 'string' && SUPABASE_PROJECT_REF_PATTERN.test(value);
}

/**
 * The exact argv for an operation. The project ref is the only variable and is
 * pattern-checked; no caller-supplied flag, path or SQL ever reaches the CLI.
 */
export function supabaseCliArgv(operation: SupabaseCliOperation, projectRef: string): readonly string[] {
  if (!isSupabaseProjectRef(projectRef)) throw new Error('project ref is not a Supabase project ref');
  switch (operation) {
    case 'migration_list':
      return ['migration', 'list', '--linked'];
    case 'db_push_dry_run':
      return ['db', 'push', '--linked', '--dry-run'];
    case 'db_push_apply':
      return ['db', 'push', '--linked', '--yes'];
    case 'link':
      return ['link', '--project-ref', projectRef];
    default:
      throw new Error('not an allowlisted Supabase CLI operation');
  }
}

// ---------------------------------------------------------------------------
// Non-secret credential metadata (what CommandOS stores).
// ---------------------------------------------------------------------------

export const SUPABASE_CLI_CREDENTIAL_HEALTH = [
  'available',
  'missing',
  'reauth_required',
  'locked',
  'unknown',
] as const;
export type SupabaseCliCredentialHealth = (typeof SUPABASE_CLI_CREDENTIAL_HEALTH)[number];

export function isSupabaseCliCredentialHealth(value: unknown): value is SupabaseCliCredentialHealth {
  return typeof value === 'string' && (SUPABASE_CLI_CREDENTIAL_HEALTH as readonly string[]).includes(value);
}

export const SUPABASE_CLI_OWNER_ACTIONS = ['enroll', 'reauth', 'unlock'] as const;
export type SupabaseCliOwnerAction = (typeof SUPABASE_CLI_OWNER_ACTIONS)[number];

export interface SupabaseCliCredentialMeta {
  /** Logical ref, e.g. supabase/<ref>/production/db_password. Never a value. */
  readonly credentialRef: string;
  readonly provider: 'supabase';
  readonly projectRef: string;
  readonly environment: 'production';
  readonly credentialKind: 'db_password';
  readonly connectionMode: 'node_bound';
  readonly designatedNodeId: string;
  readonly health: SupabaseCliCredentialHealth;
  readonly pendingOwnerAction: SupabaseCliOwnerAction | null;
  readonly enrolledAt: string | null;
  readonly lastVerifiedAt: string | null;
  readonly rotatedAt: string | null;
  readonly revokedAt: string | null;
}

export function supabaseCliCredentialRef(projectRef: string): string {
  if (!isSupabaseProjectRef(projectRef)) throw new Error('project ref is not a Supabase project ref');
  return `supabase/${projectRef}/production/db_password`;
}

/** Keychain service/account for a project. Names only — the item holds the value. */
export function supabaseCliKeychainItem(projectRef: string): { readonly service: string; readonly account: string } {
  if (!isSupabaseProjectRef(projectRef)) throw new Error('project ref is not a Supabase project ref');
  return { service: 'brunel.supabase-cli', account: `${projectRef}:production:db_password` };
}

// ---------------------------------------------------------------------------
// Connector-first router.
// ---------------------------------------------------------------------------

/**
 * What a requester wants done. Connector intents never launch the CLI while the
 * connector is available; CLI intents are the exact-repository-version cases.
 */
export const SUPABASE_CONNECTOR_INTENTS = {
  inspect_schema: 'list_tables',
  run_sql_query: 'execute_sql',
  list_remote_migrations: 'list_migrations',
  read_logs: 'get_logs',
  project_status: 'get_project',
  list_extensions: 'list_extensions',
  generate_types: 'generate_typescript_types',
  security_advisors: 'get_advisors',
} as const;
export type SupabaseConnectorIntent = keyof typeof SUPABASE_CONNECTOR_INTENTS;

export const SUPABASE_CLI_INTENTS = {
  /** Local repository versions vs remote history — the connector sees only remote. */
  compare_repo_migrations: 'migration_list',
  preview_repo_migrations: 'db_push_dry_run',
  /** Exact repository timestamps. Never re-stamped through connector apply_migration. */
  apply_repo_migrations_exact: 'db_push_apply',
  link_project: 'link',
} as const satisfies Record<string, SupabaseCliOperation>;
export type SupabaseCliIntent = keyof typeof SUPABASE_CLI_INTENTS;

export type SupabaseIntent = SupabaseConnectorIntent | SupabaseCliIntent;

export function isSupabaseIntent(value: unknown): value is SupabaseIntent {
  return typeof value === 'string'
    && (Object.hasOwn(SUPABASE_CONNECTOR_INTENTS, value) || Object.hasOwn(SUPABASE_CLI_INTENTS, value));
}

export interface SupabaseRouteInput {
  readonly intent: string;
  /** Project the requester wants to act on. */
  readonly projectRef: string;
  readonly connectorAvailable: boolean;
  /** This node's id; the broker only runs where the credential was enrolled. */
  readonly localNodeId: string;
  /** CommandOS metadata for the production credential, or null when none is designated. */
  readonly credential: Pick<
    SupabaseCliCredentialMeta,
    'projectRef' | 'designatedNodeId' | 'health' | 'revokedAt'
  > | null;
}

export type SupabaseRouteDecision =
  | { readonly route: 'connector'; readonly tool: string }
  | { readonly route: 'brokered_cli'; readonly operation: SupabaseCliOperation }
  | {
      readonly route: 'owner_action';
      readonly action: SupabaseCliOwnerAction;
      /** Stable key: repeated requests name the same single owner action. */
      readonly actionKey: string;
      readonly message: string;
    }
  | {
      readonly route: 'refused';
      readonly reason:
        | 'unknown_intent'
        | 'invalid_project_ref'
        | 'connector_unavailable'
        | 'wrong_project'
        | 'wrong_node';
      readonly message: string;
    };

export function supabaseOwnerActionKey(action: SupabaseCliOwnerAction, projectRef: string): string {
  return `supabase-cli:${action}:${projectRef}`;
}

function ownerAction(action: SupabaseCliOwnerAction, projectRef: string): SupabaseRouteDecision {
  const message = action === 'enroll'
    ? 'Supabase CLI credential enrollment required: the owner enrolls the production DB password once on the designated computer.'
    : action === 'reauth'
      ? 'Supabase CLI credential was rejected: the owner re-enrolls the production DB password on the designated computer.'
      : 'The OS credential store on the designated computer is locked: the owner unlocks it, then tests the credential.';
  return { route: 'owner_action', action, actionKey: supabaseOwnerActionKey(action, projectRef), message };
}

/**
 * Deterministic routing. Pure: same input, same decision. It never asks for a
 * password; a missing or bad credential yields one stable owner action.
 */
export function routeSupabaseRequest(input: SupabaseRouteInput): SupabaseRouteDecision {
  if (!isSupabaseIntent(input.intent)) {
    return { route: 'refused', reason: 'unknown_intent', message: 'not a supported Supabase intent' };
  }
  if (!isSupabaseProjectRef(input.projectRef)) {
    return { route: 'refused', reason: 'invalid_project_ref', message: 'not a Supabase project ref' };
  }

  let operation: SupabaseCliOperation;
  if (Object.hasOwn(SUPABASE_CONNECTOR_INTENTS, input.intent)) {
    const intent = input.intent as SupabaseConnectorIntent;
    if (input.connectorAvailable) return { route: 'connector', tool: SUPABASE_CONNECTOR_INTENTS[intent] };
    // Only remote migration history has an allowlisted CLI equivalent.
    if (intent !== 'list_remote_migrations') {
      return {
        route: 'refused',
        reason: 'connector_unavailable',
        message: 'the Supabase connector is unavailable and this is not an allowlisted CLI operation',
      };
    }
    operation = 'migration_list';
  } else {
    operation = SUPABASE_CLI_INTENTS[input.intent as SupabaseCliIntent];
  }

  const credential = input.credential;
  if (credential === null || credential.revokedAt !== null) return ownerAction('enroll', input.projectRef);
  if (credential.projectRef !== input.projectRef) {
    return {
      route: 'refused',
      reason: 'wrong_project',
      message: 'the enrolled production credential belongs to a different project',
    };
  }
  if (credential.designatedNodeId !== input.localNodeId) {
    return {
      route: 'refused',
      reason: 'wrong_node',
      message: 'the credential is bound to another computer; run this on the designated node',
    };
  }
  switch (credential.health) {
    case 'missing':
      return ownerAction('enroll', input.projectRef);
    case 'reauth_required':
      return ownerAction('reauth', input.projectRef);
    case 'locked':
      return ownerAction('unlock', input.projectRef);
    case 'available':
    case 'unknown':
      return { route: 'brokered_cli', operation };
  }
}

// ---------------------------------------------------------------------------
// Broker results and output hygiene.
// ---------------------------------------------------------------------------

export const SUPABASE_CLI_RUN_STATUSES = [
  'succeeded',
  'failed',
  'reauth_required',
  'enrollment_required',
  'locked',
  'wrong_node',
  'wrong_project',
  'refused',
  'timed_out',
] as const;
export type SupabaseCliRunStatus = (typeof SUPABASE_CLI_RUN_STATUSES)[number];

export const SUPABASE_CLI_OUTPUT_LIMIT = 16_000;

/**
 * Remove every occurrence of the secret (raw and URL-encoded) from provider
 * output, then bound it. Generic credential redaction is layered on by the node.
 */
export function scrubExactSecret(text: string, secret: string): string {
  if (secret.length === 0) return text;
  let out = text;
  const variants = new Set([secret, encodeURIComponent(secret)]);
  for (const variant of variants) {
    if (variant.length > 0) out = out.split(variant).join('[redacted]');
  }
  return out;
}

export function boundOutput(text: string, limit = SUPABASE_CLI_OUTPUT_LIMIT): string {
  if (text.length <= limit) return text;
  return `…${text.slice(text.length - limit + 1)}`;
}

/** Postgres/CLI wording for a rejected password. Drives reauth_required, never a retry loop. */
export function looksLikeRejectedPassword(output: string): boolean {
  return /password authentication failed|invalid password|SASL authentication failed|28P01/i.test(output);
}

/** Label for who asked. Bounded and plain so it is safe in audit rows. */
export const SUPABASE_CLI_REQUESTER_PATTERN = /^[A-Za-z0-9_.:@-]{1,80}$/;
